import React from 'react';
import { clsx } from 'clsx';
import {
  InboxIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  DocumentIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

export type EmptyStateType =
  | 'empty'
  | 'search'
  | 'error'
  | 'folder'
  | 'document'
  | 'users'
  | 'deals'
  | 'success'
  | 'clients'
  | 'tasks'
  | 'custom';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: EmptyStateType;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const typeIcons: Record<Exclude<EmptyStateType, 'custom'>, React.FC<{ className?: string }>> = {
  empty: InboxIcon,
  search: MagnifyingGlassIcon,
  error: ExclamationTriangleIcon,
  folder: FolderOpenIcon,
  document: DocumentIcon,
  users: UserGroupIcon,
  deals: CurrencyDollarIcon,
  success: CheckCircleIcon,
  clients: UsersIcon,
  tasks: ClipboardDocumentListIcon,
};

const typeColors: Record<Exclude<EmptyStateType, 'custom'>, { bg: string; icon: string }> = {
  empty: { bg: 'bg-neutral-100', icon: 'text-neutral-400' },
  search: { bg: 'bg-primary-50', icon: 'text-primary-400' },
  error: { bg: 'bg-error-50', icon: 'text-error-400' },
  folder: { bg: 'bg-warning-50', icon: 'text-warning-400' },
  document: { bg: 'bg-secondary-50', icon: 'text-secondary-400' },
  users: { bg: 'bg-success-50', icon: 'text-success-400' },
  deals: { bg: 'bg-success-50', icon: 'text-success-400' },
  success: { bg: 'bg-success-50', icon: 'text-success-400' },
  clients: { bg: 'bg-primary-50', icon: 'text-primary-400' },
  tasks: { bg: 'bg-warning-50', icon: 'text-warning-400' },
};

const sizeStyles: Record<string, { container: string; icon: string; iconWrapper: string; title: string; description: string }> = {
  sm: {
    container: 'py-6 px-4',
    iconWrapper: 'w-12 h-12 mb-3',
    icon: 'w-6 h-6',
    title: 'text-sm',
    description: 'text-xs',
  },
  md: {
    container: 'py-10 px-6',
    iconWrapper: 'w-16 h-16 mb-4',
    icon: 'w-8 h-8',
    title: 'text-base',
    description: 'text-sm',
  },
  lg: {
    container: 'py-16 px-8',
    iconWrapper: 'w-20 h-20 mb-5',
    icon: 'w-10 h-10',
    title: 'text-lg',
    description: 'text-base',
  },
};

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      type = 'empty',
      icon,
      title,
      description,
      action,
      size = 'md',
      className,
      ...props
    },
    ref
  ) => {
    const Icon = type !== 'custom' ? typeIcons[type] : null;
    const colors = type !== 'custom' ? typeColors[type] : { bg: 'bg-neutral-100', icon: 'text-neutral-400' };
    const styles = sizeStyles[size];

    return (
      <div
        ref={ref}
        className={clsx(
          'flex flex-col items-center justify-center text-center',
          styles.container,
          className
        )}
        {...props}
      >
        {/* Icon */}
        <div
          className={clsx(
            'rounded-full flex items-center justify-center',
            colors.bg,
            styles.iconWrapper
          )}
        >
          {icon || (Icon && <Icon className={clsx(colors.icon, styles.icon)} />)}
        </div>

        {/* Title */}
        <h3 className={clsx('font-semibold text-neutral-900', styles.title)}>
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className={clsx('text-neutral-500 mt-1 max-w-sm', styles.description)}>
            {description}
          </p>
        )}

        {/* Action */}
        {action && <div className="mt-4">{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

// Preset empty states for common scenarios
export const NoDataEmpty: React.FC<{ onAction?: () => void; actionText?: string }> = ({
  onAction,
  actionText = 'Add New',
}) => (
  <EmptyState
    type="empty"
    title="No data yet"
    description="Get started by creating your first item."
    action={
      onAction && (
        <button onClick={onAction} className="btn-primary">
          {actionText}
        </button>
      )
    }
  />
);

export const SearchEmpty: React.FC<{ query?: string }> = ({ query }) => (
  <EmptyState
    type="search"
    title="No results found"
    description={
      query
        ? `We couldn't find anything matching "${query}". Try different keywords.`
        : 'Try adjusting your search or filter criteria.'
    }
  />
);

export const ErrorEmpty: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <EmptyState
    type="error"
    title="Something went wrong"
    description="We encountered an error while loading. Please try again."
    action={
      onRetry && (
        <button onClick={onRetry} className="btn-secondary">
          Retry
        </button>
      )
    }
  />
);

export default EmptyState;

